import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Buttons';
import { getProductsQueryOptions } from '@/queries/products';
import { useSelector } from '@/redux/helper';
import { useCurrentStreamEvent } from '@/routes/habitat/hooks';

import ProductEligibilityResult from './ProductEligibilityResult';
import ProductPrice from './ProductPrice';
import useEligibilityBlocked from './useEligibilityBlocked';
import useProductCheckout from './useProductCheckout';

interface SingleHabitatDayPassProps {
  habitatId: string;
  entryPoint?: string;
}

const SingleHabitatDayPass = ({ habitatId, entryPoint }: SingleHabitatDayPassProps) => {
  const habitat = useSelector((state) => state.allHabitats.find((habitat) => habitat._id === habitatId));
  // Offer id carried in from the `ppto` param (already lowercased); redeemed as the checkout `offer`.
  // Snapshotted by the flow when it opened, because the URL it came from does not survive the
  // journey. Undefined when this card is rendered outside a flow, which is the honest answer: there
  // was no link asking for an offer.
  const offerId = useSelector((state) => state.modals.speedyFlow?.offerId);
  const { data: product } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey: 'single-habitat-day-pass',
    }),
    select: (data) => data.list[0],
  });
  const eligibilityBlocked = useEligibilityBlocked(product);
  const { start: startCheckout, isPending } = useProductCheckout();

  const [schedule, now] = useCurrentStreamEvent(habitat?._id);

  if (!habitat) return null;

  // `online` is the computed liveness (a camera reporting on); `isStreamOn` is a separate
  // persisted flag and is not the same thing. Reading the latter marked genuinely live habitats
  // "Unavailable" — and did so mostly for signed-out visitors, whose habitat list does not carry
  // it, which made an availability bug look like an eligibility one.
  const isOnline = habitat.online ?? habitat.isStreamOn;
  const isEndOfDay =
    isOnline && schedule && Date.parse(schedule.stopTime) < now + 3_600_000 && now > Date.parse(schedule.startTime);
  const isAsleep = !isOnline && !!schedule;
  const isOffline = !isOnline && !schedule;

  return (
    <div
      className="flex w-full max-w-[336px] flex-col items-center justify-center gap-3 rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]"
      data-component="single-habitat-day-pass"
    >
      <div className="relative h-[104px] w-full rounded-t-2xl bg-gray-400">
        {habitat.backgroundImage && (
          <img
            src={habitat.backgroundImage}
            alt={habitat.title}
            className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
          />
        )}
        {isOnline && !isEndOfDay && (
          <span className="absolute right-3 top-2 flex rounded-md bg-olive-3 px-2 py-1 text-[10px]/none font-bold text-hunter-green-3">
            Online
          </span>
        )}
        <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
          <div className="relative">
            {habitat.profileImage ? (
              <img
                src={habitat.profileImage}
                alt={habitat.title}
                className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
              />
            ) : (
              <div
                data-available={!isOffline}
                className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white"
              >
                {habitat.title.charAt(0)}
              </div>
            )}
            {isOffline && <div className="absolute inset-0 rounded-full bg-white/50" />}
            {habitat.zoo && (
              <div className="absolute bottom-0 right-0 rounded-full bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                <img
                  src={habitat.zoo.logo}
                  alt={habitat.zoo.name}
                  className="preflight preflight-img size-[44px] overflow-hidden rounded-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <h4 className="preflight preflight-h4 mx-4 max-w-[304px] pt-7 text-center text-2xl/7 font-bold text-green-900">
        Single Day Pass
      </h4>
      {isEndOfDay ? (
        <span className="flex rounded-md bg-rose-700 px-2 py-1 text-[10px]/none font-bold text-white">End of Day</span>
      ) : isAsleep ? (
        <span className="flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white">Asleep</span>
      ) : isOffline ? (
        <span className="flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white">Offline</span>
      ) : null}
      <p className="preflight preflight-p mx-4 max-w-[274px] text-center text-base/[18px] text-gray-500">
        24h Access to One Habitat.
        <br />
        You Selected <strong>{habitat.title}</strong>
      </p>
      <ProductPrice currency={product?.price.currency} price={product?.price.price} className="px-4" />
      <div className="w-full px-4 pb-4">
        <Button
          data-component="checkout-button"
          disabled={!product || isOffline || isPending || eligibilityBlocked}
          loading={isPending}
          size="lg"
          className="w-full"
          onClick={() => {
            if (!product) return;
            startCheckout({
              productId: product._id,
              priceId: product.price.priceId,
              // Generic product: the habitat it unlocks is attached here, per checkout.
              associatedHabitatIds: [habitat._id],
              successPath: `/h/${habitat.zoo.slug}/${habitat.slug}`,
              cancelPath: `/h/${habitat.zoo.slug}/${habitat.slug}`,
              offerId,
              productLookupKey: product.productLookupKey,
              entryPoint,
            });
          }}
        >
          {/*
            NOTE: the "Get it for free" label keys off offerId alone, not real offer eligibility, so a
            user who already redeemed `explorer` still sees it and then hits a 400 "Offer not available"
            on checkout. Accepted for now (simpler than threading eligibility through the product query);
            revisit if the dead-end checkout proves confusing — surface eligibility and fall back to
            "Select Pass" / disable when the offer is spent.
          */}
          {isOffline ? 'Unavailable' : offerId === 'explorer' ? 'Get it for free' : 'Select Pass'}
        </Button>
        {product && <ProductEligibilityResult result={product.eligibility} className="mt-3" />}
      </div>
    </div>
  );
};

export default SingleHabitatDayPass;
